import { useEffect, useMemo, useRef, useState } from "react";
import { BookingDestinationNote } from "./components/BookingDestinationNote";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Info,
  MoonStar,
  Sun,
  Sunrise,
} from "lucide-react";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import { patientsApi } from "../../shared/api/patients";
import { apiError } from "../../shared/api/client";
import { SlotConflictHint } from "../../shared/ui/SlotConflictHint";
import {
  type SlotChoice,
  bookingDurationMinutes,
  doctorSlotClashes,
  slotChoicesFromDay,
  takenStartsFor,
} from "./slotOccupancy";
import { eligibleRoomDoctors } from "./roomDoctors";
import { isoDate } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { PatientLink } from "../../shared/ui/PatientLink";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  schedulesApi,
  schedulesQueryKeys,
  timeForInput,
} from "../../shared/api/schedules";
import {
  PatientCombobox,
  type PatientChoice,
} from "./components/PatientCombobox";
import { SearchSelect } from "./components/SearchSelect";
import {
  clinicHolidaysApi,
  clinicHolidayQueryKeys,
} from "../../shared/api/clinicHolidays";
import { departmentsApi, departmentQueryKeys } from "../../shared/api/departments";
import { roomsApi, roomQueryKeys } from "../../shared/api/rooms";
import {
  workShiftsApi,
  workShiftsQueryKeys,
} from "../../shared/api/workShifts";
import {
  clinicSessionsApi,
  clinicSessionsQueryKeys,
} from "../../shared/api/clinicSessions";
import {
  useBookingDimensions,
  validateBookingDimensions,
  type BookingDimension,
} from "../../shared/hooks/useBookingDimensions";
import { hisIntegrationApi, hisIntegrationQueryKeys } from "../../shared/api/hisIntegration";
import { firstOpenSessionStart, hasEnded, hhmm } from "./sessionWindow";
import { sessionCardStates } from "./sessionCards";
import { TimeInput } from "../../shared/ui/TimeInput";
import {
  bookingAvailabilityApi,
  bookingSettingsQueryKeys,
} from "../../shared/api/bookingSettings";

/**
 * Ngày làm việc kế tiếp kể từ MAI (ngày hiện tại + 1), khớp giờ bắt đầu ca và TRÁNH ngày nghỉ.
 * Có bác sĩ → dùng availability của bác sĩ (bỏ ngày nghỉ phép / không có lịch, lấy giờ slot đầu).
 * Không có bác sĩ → mai, giờ làm việc mặc định.
 */
async function nextWorkingSlot(
  doctorId: string,
  holidays: Set<string>
): Promise<{ date: string; time: string }> {
  const base = new Date();
  base.setDate(base.getDate() + 1); // ngày hiện tại + 1
  for (let i = 0; i < 30; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const ds = isoDate(d);
    if (holidays.has(ds)) continue; // phòng khám nghỉ chung → bỏ qua
    if (!doctorId) return { date: ds, time: "08:00" }; // ngày làm việc đầu (không chọn bác sĩ)
    try {
      const av = await schedulesApi.availability(doctorId, ds);
      if (av.onLeave) continue; // bác sĩ nghỉ phép → thử ngày kế
      const slot = av.slots.find((s) => s.available);
      if (slot) return { date: ds, time: slot.startTime.slice(0, 5) }; // giờ bắt đầu làm việc
    } catch {
      /* không lấy được lịch → thử ngày kế */
    }
  }
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return { date: isoDate(t), time: "08:00" };
}

export function AppointmentCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const preselectId = params.get("patientId");
  const [patient, setPatient] = useState<PatientChoice | null>(null);

  // "Đặt lịch cho người thân" — preselect the patient passed via ?patientId=.
  useEffect(() => {
    if (!preselectId || patient) return;
    let alive = true;
    patientsApi
      .get(preselectId)
      .then((p) => {
        if (!alive) return;
        setPatient({
          id: p.id,
          name: p.fullName,
          code: p.patientCode ?? "",
          phone: p.phoneE164 ?? null,
          dob: p.dateOfBirth ?? null,
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [preselectId, patient]);
  const [doctorId, setDoctorId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [date, setDate] = useState(isoDate(new Date()));
  const [time, setTime] = useState("08:30");
  const [notes, setNotes] = useState("");
  // Hình thức khám (#18673) — mặc định "Dịch vụ" (lựa chọn phổ biến nhất); "" vẫn hợp lệ khi
  // chiều đang OPTIONAL (ví dụ sau khi bật "Vào quầy đăng ký" MQ thì bị reset về "").
  const [paymentType, setPaymentType] = useState<"" | "BHYT" | "SERVICE">("SERVICE");
  const [bhytCardNo, setBhytCardNo] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [prefillNote, setPrefillNote] = useState<string | null>(null);
  const skipPrefillRef = useRef(false);

  // #18561: restore the appointment draft saved right before jumping to
  // /patients/new, so the entered doctor/service/date/time/notes survive the
  // round-trip. The new patient itself comes back via ?patientId=.
  useEffect(() => {
    const raw = sessionStorage.getItem("appt-create-draft");
    if (!raw) return;
    sessionStorage.removeItem("appt-create-draft");
    try {
      const d = JSON.parse(raw) as Partial<{
        doctorId: string;
        serviceId: string;
        date: string;
        time: string;
        notes: string;
      }>;
      if (d.doctorId) setDoctorId(d.doctorId);
      if (d.serviceId) setServiceId(d.serviceId);
      if (d.date) setDate(d.date);
      if (d.time) setTime(d.time);
      if (d.notes) setNotes(d.notes);
      // Keep the restored date/time — the last-appointment prefill must not overwrite them.
      skipPrefillRef.current = true;
    } catch {
      /* ignore a malformed draft */
    }
  }, []);

  // Save the current appointment draft, then open the create-patient screen. It
  // returns to /appointments/new?patientId=<new id> (see PatientCreatePage).
  function goCreatePatient() {
    sessionStorage.setItem(
      "appt-create-draft",
      JSON.stringify({ doctorId, serviceId, date, time, notes }),
    );
    navigate("/patients/new?returnTo=/appointments/new");
  }

  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
  });
  const services = useQuery({
    queryKey: appointmentQueryKeys.services(),
    queryFn: () => appointmentsApi.listServices(),
  });

  // Booking Policy — hiện/ẩn/bắt buộc + thứ tự 4 chiều (Khoa/Phòng/Bác sĩ/Dịch vụ).
  const { policy, dimensions, byKey } = useBookingDimensions();
  // #19943 — Theo giờ: một lịch chiếm đúng 1 khung; Theo buổi: null (không xét trùng giờ bác sĩ).
  const slotMinutes =
    policy?.timeMode === "SLOT" && policy.slotMinutes > 0 ? policy.slotMinutes : null;
  // Thời lượng chỉ là ước tính (giờ dự kiến xong), không chặn khung kế tiếp.
  const durationMinutes = bookingDurationMinutes(
    services.data?.find((s) => s.id === serviceId)?.durationMinutes,
    slotMinutes
  );

  // #18674: tenant kết nối MQ + tick "Vào quầy đăng ký" → ẩn Khoa/Phòng, chỉ còn chọn ngày
  // (MQ Counter dùng postBookingUnpaid, không cần khoa/phòng — xử lý thủ công tại quầy sau).
  const hisBinding = useQuery({
    queryKey: hisIntegrationQueryKeys.binding(),
    queryFn: () => hisIntegrationApi.get(),
    staleTime: 5 * 60_000,
  });
  const isMqBound = hisBinding.data?.hisType === "MQ" && (hisBinding.data?.enabled ?? false);
  const [mqCounter, setMqCounter] = useState(false);
  useEffect(() => {
    if (mqCounter) {
      setDepartmentId("");
      setRoomId("");
      setPaymentType("");
      setBhytCardNo("");
    }
  }, [mqCounter]);
  const hideDeptRoomForCounter = isMqBound && mqCounter;

  // Đè "visible" của Khoa/Phòng về false khi đang Vào quầy MQ — dùng chung cho render lẫn validate.
  const effectiveDimensions = useMemo(
    () =>
      dimensions.map((d) =>
        hideDeptRoomForCounter && (d.key === "department" || d.key === "room")
          ? { ...d, visible: false, required: false }
          : d
      ),
    [dimensions, hideDeptRoomForCounter]
  );
  const visibleDims = useMemo(
    () => effectiveDimensions.filter((d) => d.visible),
    [effectiveDimensions]
  );
  const showDepartment = (byKey.department?.visible ?? false) && !hideDeptRoomForCounter;
  const showRoom = (byKey.room?.visible ?? false) && !hideDeptRoomForCounter;
  // Vào quầy MQ: hình thức khám cũng xử lý thủ công tại quầy, endpoint postBookingUnpaid không
  // mang tín hiệu này — ẩn luôn thẻ Hình thức khám, khớp đúng "chỉ còn chọn ngày".
  const showPaymentType =
    (policy?.dimPaymentType ?? "HIDDEN") !== "HIDDEN" && !hideDeptRoomForCounter;
  const paymentTypeRequired = policy?.dimPaymentType === "REQUIRED";

  // Định danh bệnh nhân (CCCD/BHYT trên hồ sơ) — chỉ cần khi chiều hình thức khám đang hiển thị,
  // để validate "BHYT thiếu cả thẻ lẫn CCCD" và gợi ý thẻ BHYT đã có sẵn.
  const patientIdentifiers = useQuery({
    queryKey: ["patient-identifiers", patient?.id],
    queryFn: () => patientsApi.listIdentifiers(patient!.id),
    enabled: Boolean(showPaymentType && patient?.id),
  });
  const patientCccd = useMemo(
    () => patientIdentifiers.data?.find((i) => i.identifierType === "CCCD")?.value ?? null,
    [patientIdentifiers.data]
  );
  const patientBhytOnFile = useMemo(
    () => patientIdentifiers.data?.find((i) => i.identifierType === "BHYT")?.value ?? null,
    [patientIdentifiers.data]
  );
  // Đổi bệnh nhân → thẻ BHYT gõ tay không còn phù hợp; xoá để tránh gán nhầm thẻ của người trước.
  useEffect(() => {
    setBhytCardNo("");
  }, [patient?.id]);
  // Chọn hình thức khám BHYT → tự điền Số thẻ BHYT: ưu tiên thẻ BHYT đã có trên hồ sơ, không có
  // thì dùng CCCD thay thế. Chỉ tự điền khi ô đang trống (không ghi đè giá trị người dùng đã gõ/xoá).
  useEffect(() => {
    if (paymentType !== "BHYT" || bhytCardNo) return;
    if (patientBhytOnFile) setBhytCardNo(patientBhytOnFile);
    else if (patientCccd) setBhytCardNo(patientCccd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, patientBhytOnFile, patientCccd]);

  // Danh mục Khoa / Phòng — chỉ tải khi chiều tương ứng đang hiển thị.
  const departments = useQuery({
    queryKey: departmentQueryKeys.options(),
    queryFn: () => departmentsApi.options(),
    enabled: showDepartment || showRoom,
  });
  const rooms = useQuery({
    queryKey: roomQueryKeys.list(true),
    queryFn: () => roomsApi.list(true),
    enabled: showRoom,
  });
  const clinicSessions = useQuery({
    queryKey: clinicSessionsQueryKeys.all(),
    queryFn: () => clinicSessionsApi.list(),
    enabled: policy?.timeMode === "SESSION",
  });

  // Phòng lọc theo khoa đã chọn (phòng không thuộc khoa nào vẫn luôn hiện).
  const roomOptions = useMemo(() => {
    const all = rooms.data ?? [];
    const filtered = departmentId
      ? all.filter((r) => !r.departmentId || r.departmentId === departmentId)
      : all;
    return filtered.map((r) => ({
      value: r.id,
      label: r.name,
      sub: r.doctorName ?? undefined,
    }));
  }, [rooms.data, departmentId]);

  // Đổi khoa mà phòng đang chọn không còn hợp lệ → bỏ chọn phòng.
  useEffect(() => {
    if (!roomId || !departmentId) return;
    const room = (rooms.data ?? []).find((r) => r.id === roomId);
    if (room && room.departmentId && room.departmentId !== departmentId) {
      setRoomId("");
    }
  }, [departmentId, roomId, rooms.data]);

  // Lịch trực (#18786) — bác sĩ được phân công cho phòng này vào ngày/giờ này.
  // Lễ tân không phải nhớ lịch trực: chọn phòng + ngày + giờ là hệ thống điền sẵn bác sĩ.
  const roomDuty = useQuery({
    queryKey: workShiftsQueryKeys.duty(roomId, date, time),
    queryFn: () => workShiftsApi.duty(roomId, date, time),
    enabled: Boolean(roomId && date),
    staleTime: 60_000,
  });
  // Chỉ tự điền khi CHƯA chọn bác sĩ. Người dùng đã chọn tay thì luôn thắng — lịch trực là gợi ý
  // mặc định, không phải ràng buộc. #19943: nhiều bác sĩ trực cùng phòng thì điền bác sĩ trực MẶC
  // ĐỊNH (cùng người tầng ghi gán khi không chọn), và phòng không có lịch trực thì giữ hành vi cũ.
  // Nhớ bác sĩ nào do HỆ THỐNG điền (#19943): đổi phòng / ngày thì người đó không còn là lựa chọn
  // của ai — bỏ đi để phòng mới điền đúng người của nó. Bác sĩ lễ tân tự chọn thì giữ (nếu còn hợp lệ).
  const autoFilledDoctorRef = useRef<string | null>(null);
  useEffect(() => {
    const auto = roomDuty.data?.autoDoctorId;
    if (auto && !doctorId) {
      setDoctorId(auto);
      autoFilledDoctorRef.current = auto;
    }
  }, [roomDuty.data?.autoDoctorId, doctorId]);
  const placeRef = useRef(`${roomId}|${date}`);
  useEffect(() => {
    const place = `${roomId}|${date}`;
    if (placeRef.current === place) return;
    placeRef.current = place;
    if (doctorId && doctorId === autoFilledDoctorRef.current) setDoctorId("");
    autoFilledDoctorRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, date]);
  // #19943 — ô Bác sĩ theo phòng: bác sĩ trực phòng đó, hoặc (phòng không ai trực) bác sĩ đang
  // không trực phòng nào. Bác sĩ đang chọn mà không còn hợp lệ cho phòng/giờ mới thì bỏ chọn — hiệu
  // ứng ở trên điền lại người tầng ghi sẽ gán.
  const doctorOptions = useMemo(
    () => eligibleRoomDoctors(doctors.data ?? [], roomId, roomDuty.data),
    [doctors.data, roomId, roomDuty.data]
  );
  useEffect(() => {
    if (!doctorId || !roomId || !roomDuty.data || !doctors.data) return;
    if (!doctorOptions.some((dd) => dd.id === doctorId)) setDoctorId("");
  }, [doctorId, roomId, roomDuty.data, doctors.data, doctorOptions]);
  // Lịch nghỉ chung của phòng khám — dùng để tránh ngày nghỉ khi tự điền + cảnh báo khi chọn.
  const holidaysQuery = useQuery({
    queryKey: clinicHolidayQueryKeys.all(),
    queryFn: () => clinicHolidaysApi.list(),
    staleTime: 5 * 60_000,
  });
  const holidaySet = useMemo(
    () => new Set((holidaysQuery.data ?? []).map((h) => h.date)),
    [holidaysQuery.data]
  );
  const selectedHoliday = useMemo(
    () => (holidaysQuery.data ?? []).find((h) => h.date === date) ?? null,
    [holidaysQuery.data, date]
  );

  // Real-time conflict check against the chosen doctor's day
  const conflictCheck = useQuery({
    queryKey: ["appointment-conflict", doctorId, date, time, slotMinutes],
    queryFn: () => appointmentsApi.forDate(date),
    enabled: Boolean(doctorId && date && time && slotMinutes),
  });

  // T84 — availability (weekly schedule minus leaves minus booked) for the picked doctor+date
  const availability = useQuery({
    queryKey: schedulesQueryKeys.availability(doctorId, date, roomId),
    queryFn: () => schedulesApi.availability(doctorId, date, roomId),
    enabled: Boolean(doctorId && date),
  });

  // #19939 TC10 — luật trước đây chỉ chặn ở bước gửi (ngoài cửa sổ đặt trước, đủ tổng lượt/ngày,
  // không còn giờ mở…) được báo NGAY khi chọn ngày, bằng cùng availability kênh bệnh nhân dùng.
  // Ngày nghỉ đã có khung báo riêng (selectedHoliday) nên không nhắc lại.
  const dayAvailability = useQuery({
    queryKey: bookingSettingsQueryKeys.day(date, departmentId, roomId, doctorId),
    queryFn: () =>
      bookingAvailabilityApi.day({ date, departmentId, roomId, doctorId }),
    enabled: Boolean(date),
    staleTime: 30_000,
  });
  const dayClosed =
    dayAvailability.data?.closed &&
    dayAvailability.data.closed.code !== "clinic_closed_holiday"
      ? dayAvailability.data.closed
      : null;

  // Real time-frame availability from the connected external HIS (LEO — Phase 6, extends #18648).
  // null (204) when the tenant isn't LEO-bound / disabled / room never synced — the doctor grid /
  // manual input below is the fallback in every one of those cases, not an error state.
  const hisTimeSlots = useQuery({
    queryKey: hisIntegrationQueryKeys.timeSlots(roomId, date),
    queryFn: () => hisIntegrationApi.timeSlots(roomId, date),
    enabled: Boolean(showRoom && roomId && date),
  });
  const leoSlots = hisTimeSlots.data?.slots ?? [];
  const hasLeoSlots = Boolean(showRoom && roomId && leoSlots.length > 0);

  // #19943 — chế độ Theo giờ: lễ tân chọn KHUNG trên lưới chia theo "Hình thức giờ", không gõ giờ.
  // Có bác sĩ → khung của bác sĩ (ca trực, nghỉ phép, "Đã đặt"); chưa chọn → availability chung
  // của ngày (Buổi khám / ca trực / giờ làm việc / sức chứa). `undefined` = đang tải; rỗng = không có
  // khung nào → lùi về ô nhập tay (bác sĩ chưa có ca trực, chưa khai Buổi khám…).
  const slotChoices = useMemo<SlotChoice[] | undefined>(() => {
    if (!slotMinutes || hasLeoSlots) return [];
    if (doctorId) {
      const av = availability.data;
      if (!av) return undefined;
      if (av.onLeave) return [];
      return av.slots.map((x) => ({
        label: timeForInput(x.startTime),
        available: x.available,
        reason: x.available ? null : (x.reason ?? "Hết chỗ"),
      }));
    }
    return dayAvailability.data ? slotChoicesFromDay(dayAvailability.data.slots) : undefined;
  }, [slotMinutes, hasLeoSlots, doctorId, availability.data, dayAvailability.data]);
  const slotGridMode =
    Boolean(slotMinutes && !hasLeoSlots) && (slotChoices === undefined || slotChoices.length > 0);
  const timeIsOpenSlot = Boolean(slotChoices?.some((c) => c.available && c.label === time));
  // Giờ khởi tạo ("08:30"), giờ tự điền hay giờ của bác sĩ trước đều có thể không phải một khung còn
  // trống của lưới đang hiện: quy về khung trống đầu tiên, hoặc xoá trắng để lễ tân buộc phải chọn —
  // cùng cách #18712 làm cho chế độ Theo buổi.
  useEffect(() => {
    if (!slotGridMode || !slotChoices || slotChoices.length === 0 || timeIsOpenSlot) return;
    setTime(slotChoices.find((c) => c.available)?.label ?? "");
  }, [slotGridMode, slotChoices, timeIsOpenSlot]);

  // #18712 — ở chế độ SESSION, `time` chỉ hợp lệ khi TRÙNG KHÍT giờ bắt đầu của một buổi, vì nút
  // buổi chỉ sáng khi `selected = time === startLabel`. Nhưng `time` khởi tạo bằng hằng "08:30" và
  // hàm tự-điền còn đặt nó theo giờ làm việc của bác sĩ — cả hai đều có thể không khớp buổi nào
  // (VD buổi Sáng bắt đầu 07:30). Khi đó giao diện KHÔNG nút nào sáng nhưng `time` vẫn khác rỗng,
  // nên validate() cũ (`if (!time)`) vẫn cho qua → đặt được lịch với giờ không thuộc booking policy.
  // Xử lý: quy giá trị đang có về buổi CHỨA nó (vẫn giữ được ích lợi của tự-điền); nếu không buổi
  // nào chứa thì xoá trắng để người dùng buộc phải chọn.
  const sessionStarts = useMemo(
    () => (clinicSessions.data ?? []).map((s) => s.startTime.slice(0, 5)),
    [clinicSessions.data]
  );
  // #19956 — Theo buổi: mỗi thẻ buổi khoá theo giới hạn (lượt/buổi/phòng, lượt/phòng/ngày) và theo
  // ca trực của bác sĩ đang chọn — cùng nguồn availability với tầng ghi.
  const sessionCards = useMemo(
    () =>
      policy?.timeMode === "SESSION"
        ? sessionCardStates(
            doctorId ? (availability.data?.onLeave ? [] : availability.data?.slots) : null,
            doctorId ? null : dayAvailability.data?.sessions
          )
        : new Map(),
    [policy?.timeMode, doctorId, availability.data, dayAvailability.data]
  );
  useEffect(() => {
    if (policy?.timeMode !== "SESSION" || hasLeoSlots) return;
    const sessions = clinicSessions.data ?? [];
    if (sessions.length === 0 || !time) return;
    // #19041 — một buổi ĐÃ KẾT THÚC của chính hôm nay không còn là lựa chọn hợp lệ, kể cả khi nó
    // trùng khít giờ mở buổi. Giá trị khởi tạo "08:30" và hàm tự-điền đều có thể trỏ vào đó, nên
    // chỗ này vừa quy về buổi chứa nó, vừa rời khỏi buổi đã đóng — sang buổi còn mở gần nhất, hoặc
    // xoá trắng nếu hôm nay không còn buổi nào.
    const current = sessions.find((x) => hhmm(x.startTime) === time);
    if (current && !hasEnded(current, date, new Date())) return;
    if (!current) {
      // "HH:mm" zero-pad nên so sánh chuỗi tương đương so sánh thời gian.
      const containing = sessions.find(
        (x) => time >= hhmm(x.startTime) && time < hhmm(x.endTime)
      );
      if (containing && !hasEnded(containing, date, new Date())) {
        setTime(hhmm(containing.startTime));
        return;
      }
    }
    setTime(firstOpenSessionStart(sessions, date, new Date()) ?? "");
  }, [policy?.timeMode, hasLeoSlots, clinicSessions.data, sessionStarts, time, date]);
  // Nhóm khung giờ LEO theo buổi để lưới không kéo dài lộn xộn — hiển thị full-width
  // bên dưới hàng Ngày/Giờ thay vì chen vào cột nửa-trang gây lệch chiều cao với Ngày.
  const leoSlotGroups = useMemo(() => {
    const withLabel = (hisTimeSlots.data?.slots ?? []).map((s) => ({
      ...s,
      label: timeForInput(s.startTime),
    }));
    const sang = withLabel.filter((s) => Number(s.label.slice(0, 2)) < 12);
    const chieu = withLabel.filter((s) => Number(s.label.slice(0, 2)) >= 12);
    return [
      { title: "Buổi sáng", slots: sang },
      { title: "Buổi chiều", slots: chieu },
    ].filter((g) => g.slots.length > 0);
  }, [hisTimeSlots.data]);

  // Ghi nhận lần đặt lịch cuối của bệnh nhân → tự điền bác sĩ + dịch vụ (khoa khám). Thời gian
  // KHÔNG lấy từ lịch cũ mà đặt = ngày mai, khớp giờ bắt đầu làm việc và tránh ngày nghỉ.
  useEffect(() => {
    if (!patient) return;
    // Returning from patient-create with a restored draft — keep the user's
    // entered date/time; skip the last-appointment prefill this once (#18561).
    if (skipPrefillRef.current) {
      skipPrefillRef.current = false;
      return;
    }
    let alive = true;
    (async () => {
      let lastDoctor = "";
      let lastService = "";
      try {
        const page = await appointmentsApi.list(
          { patientId: patient.id },
          { page: 0, size: 1 } // native query đã ORDER BY scheduled_start DESC → dòng đầu = gần nhất
        );
        const last = page.content?.[0];
        lastDoctor = last?.doctorId ?? "";
        lastService = last?.serviceId ?? "";
      } catch {
        /* chưa có lịch cũ → chỉ đặt thời gian */
      }
      const slot = await nextWorkingSlot(lastDoctor, holidaySet);
      if (!alive) return;
      if (lastDoctor) setDoctorId(lastDoctor);
      if (lastService) setServiceId(lastService);
      setDate(slot.date);
      setTime(slot.time);
      setPrefillNote(
        lastDoctor || lastService
          ? "Đã tự điền bác sĩ/dịch vụ theo lần đặt gần nhất. Thời gian = ngày mai, khớp giờ bắt đầu làm việc (đã tránh ngày nghỉ)."
          : "Bệnh nhân chưa có lịch trước đó — đặt vào ngày mai, giờ bắt đầu làm việc."
      );
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  const conflicts = useMemo(
    () =>
      doctorSlotClashes(
        conflictCheck.data,
        doctorId,
        new Date(`${date}T${time}:00`),
        slotMinutes
      ),
    [conflictCheck.data, doctorId, date, time, slotMinutes]
  );

  // Thẻ BHYT gửi đi: ưu tiên số vừa gõ, else số đã có sẵn trên hồ sơ bệnh nhân.
  const effectiveBhytCardNo = bhytCardNo.trim() || patientBhytOnFile || "";

  const create = useMutation({
    mutationFn: () =>
      appointmentsApi.create({
        patientId: patient!.id,
        doctorId: doctorId || null,
        serviceId: serviceId || null,
        // Chỉ gửi Khoa/Phòng khi chiều đó đang hiển thị theo booking policy.
        departmentId: showDepartment ? departmentId || null : null,
        roomId: showRoom ? roomId || null : null,
        startAt: new Date(`${date}T${time}:00`).toISOString(),
        durationMinutes,
        notes: notes || null,
        paymentType: showPaymentType && paymentType ? paymentType : null,
        bhytCardNo:
          showPaymentType && paymentType === "BHYT" && effectiveBhytCardNo
            ? effectiveBhytCardNo
            : null,
        registrationType: mqCounter ? "COUNTER" : null,
      }),
    onSuccess: (res) => {
      // #19943 — khung vừa đặt phải hiện "Đã đặt" ngay lần mở màn kế tiếp, ở MỌI tổ hợp phòng /
      // bác sĩ (lịch không chọn bác sĩ vẫn khoá khung của bác sĩ trực phòng), không đợi hết
      // staleTime 30 giây của lưới.
      void queryClient.invalidateQueries({ queryKey: ["booking-availability"] });
      void queryClient.invalidateQueries({ queryKey: ["doctor-availability"] });
      void queryClient.invalidateQueries({ queryKey: ["appointment-conflict"] });
      navigate(`/appointments/${res.id}`);
    },
    onError: (e) => setErrors({ _form: apiError(e).detail }),
    // `create.error` is what SlotConflictHint reads for suggestedSlots (#18672).
  });

  /**
   * Whether the save failed only because the slot was taken (#18787).
   *
   * <p>Drives presentation, not control flow: a taken slot is a normal outcome of hunting for a
   * free one, so it reads as information with the alternatives beside it. Every OTHER failure
   * keeps the red treatment — softening those would be the mistake this change is careful not to
   * make.
   */
  const isSlotConflict =
    Boolean(create.error) && apiError(create.error).code === "appointment_slot_conflict";

  /**
   * The doctor's own bookings for the chosen day — fetched ONLY after a slot conflict.
   *
   * <p>`enabled` matters more than it looks: reception opens this screen constantly, and the one
   * question this answers ("which cells are already full") only exists once the server has refused.
   * Loading it eagerly would put a request on every visit to pay for a line almost nobody sees.
   *
   * <p>#18681: the clinic has no recurring booking, so a standing grid (07:30 and 13:30, two
   * doctors, one day at a time) is typed in by hand. A bare "khung giờ đã có lịch hẹn khác" cannot
   * tell them whether they are colliding with a colleague or re-entering their own row.
   */
  const doctorDay = useQuery({
    queryKey: appointmentQueryKeys.forDate(date),
    queryFn: () => appointmentsApi.forDate(date),
    enabled: isSlotConflict && Boolean(doctorId) && Boolean(date),
    staleTime: 15_000,
  });
  const takenStarts = takenStartsFor(doctorDay.data, doctorId, date);

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!patient) e.patient = "Vui lòng chọn bệnh nhân";
    // Các chiều Khoa/Phòng/Bác sĩ/Dịch vụ: bắt buộc theo booking policy (REQUIRED).
    const dimErrors = validateBookingDimensions(effectiveDimensions, {
      department: departmentId,
      room: roomId,
      doctor: doctorId,
      service: serviceId,
    });
    Object.assign(e, dimErrors);
    if (!date) e.date = "Chọn ngày";
    else if (selectedHoliday) e.date = "Phòng khám nghỉ ngày này — chọn ngày khác";
    else if (dayClosed) e.date = dayClosed.message;
    // #18712 — không chỉ "khác rỗng": ở chế độ SESSION giờ phải là giờ bắt đầu của một buổi có
    // thật, nếu không lịch tạo ra sẽ không tuân theo booking policy. Đây là chốt chặn cuối; effect
    // quy-về-buổi ở trên lo phần đồng bộ giao diện.
    if (!time) e.time = "Vui lòng chọn giờ bắt đầu";
    else if (
      policy?.timeMode === "SESSION" &&
      !hasLeoSlots &&
      sessionStarts.length > 0 &&
      !sessionStarts.includes(time)
    ) {
      e.time = "Vui lòng chọn giờ bắt đầu";
    } else if (slotGridMode && slotChoices && slotChoices.length > 0 && !timeIsOpenSlot) {
      // #19943 — Theo giờ: giờ hẹn phải là một khung còn trống của lưới đang hiện.
      e.time = "Vui lòng chọn một khung giờ còn trống";
    } else if (policy?.timeMode === "SESSION" && !hasLeoSlots) {
      // #19041 — chốt cuối trước khi gửi. Máy chủ cũng từ chối (`booking_session_passed`), nhưng
      // nói ngay tại ô vừa bấm thì lễ tân biết phải đổi gì, thay vì đọc một lỗi đỏ toàn trang.
      const chosen = (clinicSessions.data ?? []).find((x) => hhmm(x.startTime) === time);
      if (chosen && hasEnded(chosen, date, new Date())) {
        e.time = `Buổi này đã kết thúc lúc ${hhmm(chosen.endTime)} — chọn buổi hoặc ngày khác`;
      } else if (sessionCards.get(time)?.blocked) {
        e.time = `${sessionCards.get(time)?.label ?? "Buổi đã hết chỗ"} — chọn buổi hoặc ngày khác`;
      } else if (doctorId && sessionCards.get(time)?.offDuty) {
        e.time = "Bác sĩ không trực buổi này — chọn buổi khác hoặc bỏ chọn bác sĩ";
      }
    }
    // Thời lượng lấy tự động (#19943: dịch vụ, không có thì 1 khung) — không nhập tay ở màn đặt lịch.
    if (showPaymentType) {
      if (paymentTypeRequired && !paymentType) {
        e.paymentType = "Vui lòng chọn hình thức khám";
      }
      // Quyết định chủ phòng khám (#18673): BHYT mà thiếu cả thẻ BHYT lẫn CCCD → chặn tại form,
      // không để lộ lỗi ở bước đẩy HIS nền.
      if (paymentType === "BHYT" && !effectiveBhytCardNo && !patientCccd) {
        e.bhytCardNo =
          "Bệnh nhân chưa có số thẻ BHYT và cũng chưa có CCCD trên hồ sơ — nhập số thẻ BHYT hoặc bổ sung CCCD cho bệnh nhân trước.";
      }
    }
    return e;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Defence in depth (#18681). The save button carries disabled={create.isPending}, but a
    // <form> is also submitted by pressing Enter in a field, and that path never consults the
    // button. Playwright against the deployed build shows Chrome does NOT submit on Enter from
    // the date/time inputs, so this guard is not what fixed #18681 and nothing observed in
    // production needed it — it is here so the next single-line text field added to this form
    // cannot quietly reintroduce a double submit.
    if (create.isPending) return;
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length === 0) create.mutate();
  }

  // Render một chiều đặt lịch (Khoa/Phòng/Bác sĩ/Dịch vụ) — thứ tự do booking policy quyết định.
  function renderDimension(d: BookingDimension) {
    const req = d.required ? " *" : d.mode === "OPTIONAL" ? " (tuỳ chọn)" : "";
    const err = errors[d.key];
    switch (d.key) {
      case "department":
        return (
          <div key="department" className="space-y-1.5">
            <Label htmlFor="department">{d.label}{req}</Label>
            <SearchSelect
              id="department"
              value={departmentId}
              onChange={setDepartmentId}
              emptyLabel="— Không chọn khoa —"
              placeholder="Tìm khoa…"
              options={(departments.data ?? []).map((k) => ({
                value: k.id,
                label: k.name,
                sub: k.code,
              }))}
            />
            {err && <span className="text-xs text-destructive">{err}</span>}
          </div>
        );
      case "room":
        return (
          <div key="room" className="space-y-1.5">
            <Label htmlFor="room">{d.label}{req}</Label>
            <SearchSelect
              id="room"
              value={roomId}
              onChange={setRoomId}
              emptyLabel="— Chưa xếp phòng —"
              placeholder="Tìm phòng…"
              options={roomOptions}
            />
            {policy?.roomFill !== "NONE" && !d.required && (
              <p className="text-xs text-muted-foreground">
                Để trống nếu muốn {policy?.roomFill === "AUTO" ? "hệ thống" : "lễ tân"} xếp
                phòng sau.
              </p>
            )}
            {err && <span className="text-xs text-destructive">{err}</span>}
          </div>
        );
      case "doctor":
        return (
          <div key="doctor" className="space-y-1.5">
            <Label htmlFor="doctor">{d.label}{req}</Label>
            <SearchSelect
              id="doctor"
              value={doctorId}
              onChange={(next) => {
                // Lễ tân tự chọn → không còn là bác sĩ hệ thống điền.
                autoFilledDoctorRef.current = null;
                setDoctorId(next);
              }}
              emptyLabel="— Bác sĩ bất kỳ —"
              placeholder="Tìm bác sĩ theo tên / chuyên khoa…"
              options={doctorOptions.map((dd) => ({
                value: dd.id,
                label: dd.name,
                sub: dd.specialty ?? undefined,
              }))}
            />
            {roomId && roomDuty.data && (
              <p className="text-xs text-muted-foreground">
                {roomDuty.data.ambiguous
                  ? "Phòng này có nhiều bác sĩ trực khung giờ đã chọn — đã điền bác sĩ trực mặc định, có thể đổi sang bác sĩ trực khác."
                  : roomDuty.data.doctorIds.length > 0
                    ? "Đã điền theo lịch trực của phòng. Có thể đổi nếu cần."
                    : roomDuty.data.autoDoctorId
                      ? "Phòng chưa có bác sĩ trực giờ này — đã điền bác sĩ mặc định của phòng; danh sách là các bác sĩ không trực phòng nào lúc này."
                      : "Phòng không có bác sĩ trực giờ này — danh sách là các bác sĩ không trực phòng nào lúc này."}
              </p>
            )}
            {policy?.doctorFill !== "NONE" && !d.required && (
              <p className="text-xs text-muted-foreground">
                Để trống nếu muốn {policy?.doctorFill === "AUTO" ? "hệ thống" : "lễ tân"} xếp
                bác sĩ sau.
              </p>
            )}
            {err && <span className="text-xs text-destructive">{err}</span>}
          </div>
        );
      case "service":
        return (
          <div key="service" className="space-y-1.5">
            <Label htmlFor="service">{d.label}{req}</Label>
            <SearchSelect
              id="service"
              value={serviceId}
              onChange={(next) => {
                // #19943 — thời lượng suy ra từ dịch vụ (bookingDurationMinutes), không còn
                // state riêng: bỏ chọn dịch vụ thì về lại 1 khung thay vì giữ số của dịch vụ cũ.
                setServiceId(next);
              }}
              emptyLabel="— Không chọn —"
              placeholder="Tìm dịch vụ theo tên…"
              options={(services.data ?? []).map((s) => ({
                value: s.id,
                label: s.name,
                sub: s.durationMinutes ? `${s.durationMinutes} phút` : undefined,
              }))}
            />
            {err && <span className="text-xs text-destructive">{err}</span>}
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <div>
      <PageHeader
        title="Đặt lịch hẹn mới"
        description="Tìm bệnh nhân, chọn bác sĩ + dịch vụ + thời gian. Hệ thống tự kiểm tra trùng slot."
        breadcrumbs={[
          { label: "Trang chủ", to: "/dashboard" },
          { label: "Lịch hẹn", to: "/appointments" },
          { label: "Đặt lịch" },
        ]}
        actions={
          <Button asChild variant="outline">
            <Link to="/appointments">
              <ArrowLeft className="size-4" /> Lịch tuần
            </Link>
          </Button>
        }
      />

      <form
        onSubmit={onSubmit}
        noValidate
        className="mx-auto grid max-w-2xl gap-4"
      >
        <Card>
          <CardHeader>
            <CardTitle>Bệnh nhân</CardTitle>
          </CardHeader>
          <CardContent>
            <PatientCombobox
              value={patient}
              onChange={setPatient}
              autoFocus
              error={errors.patient}
              onCreateNew={goCreatePatient}
            />
          </CardContent>
        </Card>

        {isMqBound && (
          <Card>
            <CardContent className="flex items-center gap-2 pt-6">
              <Checkbox
                id="mqCounter"
                checked={mqCounter}
                onCheckedChange={(v) => setMqCounter(v === true)}
              />
              <Label htmlFor="mqCounter" className="font-normal">
                Vào quầy đăng ký
              </Label>
              <span className="text-sm text-muted-foreground">
                — chỉ đăng ký ngày khám, khoa/phòng/hình thức khám xử lý thủ công tại quầy
              </span>
            </CardContent>
          </Card>
        )}

        {visibleDims.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                {visibleDims.map((d) => d.label).join(" · ")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {prefillNote && (
                <p className="flex items-start gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
                  <Info className="mt-0.5 size-4 shrink-0" />
                  <span>{prefillNote}</span>
                </p>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                {visibleDims.map(renderDimension)}
              </div>
              {/* #19934 — nói rõ lịch này đi đâu và bệnh nhân cầm số NÀO: có phòng thì số vào
                  phòng, đi thẳng vào phòng; không phòng thì đặt vào QUẦY TIẾP ĐÓN, số quầy, lễ tân
                  chỉ định phòng. Hai số khác nhau, và người đặt phải biết mình đang cấp số nào. */}
              <BookingDestinationNote roomId={roomId} departmentId={departmentId} />
            </CardContent>
          </Card>
        )}

        {showPaymentType && (
          <div className="rounded-lg border bg-card px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Label className="text-sm font-medium text-foreground">
                Hình thức khám
                {paymentTypeRequired ? (
                  <span className="text-destructive"> *</span>
                ) : (
                  <span className="font-normal text-muted-foreground"> (tuỳ chọn)</span>
                )}
              </Label>
              <div
                role="radiogroup"
                aria-label="Hình thức khám"
                className="inline-flex rounded-md border bg-muted p-0.5"
              >
                {(
                  [
                    ["SERVICE", "Dịch vụ"],
                    ["BHYT", "BHYT"],
                  ] as const
                ).map(([value, optionLabel]) => (
                  <label
                    key={value}
                    className={cn(
                      "cursor-pointer rounded-[5px] px-3 py-1 text-sm font-medium transition-colors",
                      "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-1",
                      paymentType === value
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <input
                      type="radio"
                      name="paymentType"
                      value={value}
                      checked={paymentType === value}
                      onChange={() => setPaymentType(value)}
                      className="sr-only"
                    />
                    {optionLabel}
                  </label>
                ))}
              </div>
            </div>
            {errors.paymentType && (
              <p className="mt-2 text-xs text-destructive">{errors.paymentType}</p>
            )}
            {paymentType === "BHYT" && (
              <div className="mt-3 space-y-1.5 border-t pt-3">
                <Label htmlFor="bhytCardNo">Số thẻ BHYT</Label>
                <Input
                  id="bhytCardNo"
                  value={bhytCardNo}
                  onChange={(e) => setBhytCardNo(e.target.value)}
                  placeholder="Nhập số thẻ BHYT"
                  className="w-full sm:w-64"
                />
                <p className="text-xs text-muted-foreground">
                  {patientBhytOnFile
                    ? "Đã tự động điền theo thẻ BHYT trên hồ sơ — có thể sửa nếu cần."
                    : patientCccd
                      ? `Chưa có thẻ BHYT trên hồ sơ — đã tự động dùng CCCD (${patientCccd}) thay thế.`
                      : "Bệnh nhân chưa có thẻ BHYT lẫn CCCD trên hồ sơ — vui lòng nhập số thẻ BHYT."}
                </p>
                {errors.bhytCardNo && (
                  <p className="text-xs text-destructive">{errors.bhytCardNo}</p>
                )}
              </div>
            )}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Thời gian</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedHoliday && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>
                  Phòng khám nghỉ ngày này
                  {selectedHoliday.reason ? ` (${selectedHoliday.reason})` : ""} — vui
                  lòng chọn ngày khác.
                </span>
              </div>
            )}
            {!selectedHoliday && dayClosed && (
              <div
                role="alert"
                data-testid="day-closed"
                className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>{dayClosed.message}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="d">Ngày *</Label>
                <Input
                  id="d"
                  type="date"
                  value={date}
                  min={isoDate(new Date())}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-auto"
                />
                {errors.date && (
                  <span className="text-xs text-destructive">{errors.date}</span>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="t">Giờ bắt đầu *</Label>
                {hasLeoSlots ? (
                  <div
                    id="t"
                    className="flex h-9 w-fit items-center gap-1.5 rounded-md border border-input bg-muted/40 px-3 text-sm"
                  >
                    {time ? (
                      <>
                        <CheckCircle2 className="size-4 shrink-0 text-primary" />
                        <span className="font-medium">{time}</span>
                      </>
                    ) : (
                      <span className="whitespace-nowrap text-muted-foreground">
                        Chưa chọn giờ
                      </span>
                    )}
                  </div>
                ) : policy?.timeMode === "SESSION" ? (
                  <div
                    id="t"
                    role="radiogroup"
                    aria-label="Chọn buổi khám"
                    className="grid grid-cols-1 gap-3 pt-1.5 sm:grid-cols-3 sm:gap-4"
                  >
                    {(clinicSessions.data ?? []).map((s) => {
                      const startLabel = s.startTime.slice(0, 5);
                      const startHour = Number(s.startTime.slice(0, 2));
                      const isEvening = startHour >= 18;
                      const SessionIcon = isEvening ? MoonStar : startHour < 12 ? Sunrise : Sun;
                      const selected = time === startLabel;
                      // #19041 — buổi của CHÍNH hôm nay mà đã qua giờ kết thúc thì không bấm được
                      // nữa. Vẫn hiện (ẩn đi thì lễ tân tưởng phòng khám chưa cấu hình buổi đó),
                      // nhưng mờ, không bấm được, và NÓI RÕ nó kết thúc lúc mấy giờ.
                      const ended = hasEnded(s, date, new Date());
                      // #19956 — hết chỗ: khoá như buổi đã qua. Bác sĩ không trực buổi này: vẫn
                      // bấm được, nhưng bỏ chọn bác sĩ (lễ tân chỉ định sau), như #19954.
                      const card = sessionCards.get(startLabel);
                      const blocked = ended || Boolean(card?.blocked);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          disabled={blocked}
                          data-off-duty={card?.offDuty || undefined}
                          title={
                            ended
                              ? `Đã kết thúc lúc ${hhmm(s.endTime)}`
                              : card?.offDuty
                                ? "Bác sĩ không trực buổi này — chọn sẽ bỏ bác sĩ, lễ tân chỉ định sau"
                                : (card?.label ?? undefined)
                          }
                          onClick={() => {
                            if (card?.offDuty) setDoctorId("");
                            setTime(startLabel);
                          }}
                          className={cn(
                            "flex items-center gap-3 rounded-lg border p-4 text-left transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                            card?.offDuty && !ended && "border-dashed",
                            blocked
                              ? "cursor-not-allowed border-input bg-muted/40 opacity-60"
                              : selected
                                ? "border-primary bg-primary/5 ring-1 ring-primary"
                                : "border-input hover:bg-muted/50"
                          )}
                        >
                          <SessionIcon
                            className={cn(
                              "size-7 shrink-0",
                              ended
                                ? "text-muted-foreground"
                                : isEvening
                                  ? "text-violet-500"
                                  : "text-amber-500"
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-base font-semibold">{s.label}</div>
                            <div className="text-sm text-muted-foreground">
                              {startLabel} – {s.endTime.slice(0, 5)}
                            </div>
                            {ended ? (
                              <div className="text-sm font-medium text-muted-foreground">
                                Đã kết thúc lúc {hhmm(s.endTime)}
                              </div>
                            ) : (
                              card?.label && (
                                <div
                                  data-testid="session-card-label"
                                  className="text-sm font-medium text-muted-foreground"
                                >
                                  {card.label}
                                </div>
                              )
                            )}
                          </div>
                          {selected && !ended ? (
                            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                              <Check className="size-3.5" strokeWidth={3} />
                            </span>
                          ) : (
                            <span className="size-5 shrink-0 rounded-full border-2 border-muted-foreground/30" />
                          )}
                        </button>
                      );
                    })}
                    {clinicSessions.data?.length === 0 && (
                      <p className="text-sm text-muted-foreground sm:col-span-3">
                        Chưa cấu hình buổi khám — vào Cài đặt · Vận hành · Buổi khám.
                      </p>
                    )}
                  </div>
                ) : slotGridMode ? (
                  <div
                    id="t"
                    className="flex h-9 w-fit items-center gap-1.5 rounded-md border border-input bg-muted/40 px-3 text-sm"
                  >
                    {time ? (
                      <>
                        <CheckCircle2 className="size-4 shrink-0 text-primary" />
                        <span className="font-medium">{time}</span>
                      </>
                    ) : (
                      <span className="whitespace-nowrap text-muted-foreground">
                        Chưa chọn khung giờ
                      </span>
                    )}
                  </div>
                ) : (
                  <TimeInput id="t" value={time} onChange={setTime} className="w-32" />
                )}
                {errors.time && (
                  <span className="text-xs text-destructive">{errors.time}</span>
                )}
              </div>
            </div>
            {hasLeoSlots && (
              <div className="space-y-2">
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <Info className="mt-0.5 size-3.5 shrink-0" />
                  <span>
                    Phòng này do hệ thống HIS quản lý giờ khám — chỉ chọn được khung
                    giờ HIS cấp bên dưới để tránh lệch giờ khi đẩy lịch sang HIS.
                  </span>
                </p>
                <div className="max-h-72 overflow-y-auto rounded-md border p-3">
                  {leoSlotGroups.map((group) => (
                    <div key={group.title} className="mb-3 last:mb-0">
                      <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                        {group.title}
                      </p>
                      <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-8">
                        {group.slots.map((s) => {
                          const selected = s.label === time;
                          return (
                            <Button
                              key={s.startTime}
                              type="button"
                              size="sm"
                              variant={selected ? "default" : "outline"}
                              disabled={!s.available}
                              onClick={() => setTime(s.label)}
                              title={s.available ? "Còn chỗ" : "Hết chỗ"}
                            >
                              {s.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {!time && (
                  <p className="text-xs text-destructive">
                    Chưa chọn khung giờ — bấm một khung giờ ở trên.
                  </p>
                )}
              </div>
            )}
            {!hasLeoSlots && (
              doctorId &&
              availability.data && (
              <div className="pt-1">
                {availability.data.onLeave ? (
                  <div
                    className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                    role="alert"
                  >
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    <span>
                      <strong>Bác sĩ nghỉ ngày này.</strong>
                      {availability.data.leaveReason
                        ? ` Lý do: ${availability.data.leaveReason}.`
                        : ""}
                    </span>
                  </div>
                ) : policy?.timeMode === "SESSION" ? null : availability.data.slots.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {/* #19939 — nói đúng lý do server sẽ trả, không đoán "chưa có lịch". */}
                    {availability.data.closedMessage && !dayClosed
                      ? availability.data.closedMessage
                      : dayClosed
                        ? null
                        : "Bác sĩ chưa có lịch làm việc cho thứ này. Có thể chọn giờ thủ công nếu cần."}
                  </p>
                ) : (
                  <div>
                    <p className="mb-1.5 text-sm text-muted-foreground">
                      Khung giờ của Buổi khám — bấm để chọn:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {availability.data.slots.map((s) => {
                        const label = timeForInput(s.startTime);
                        const selected = label === time;
                        // #19954 — khung ngoài giờ trực của bác sĩ đang chọn: vẫn bấm được, nhưng
                        // bỏ chọn bác sĩ — lịch đặt không bác sĩ, lễ tân chỉ định sau. Không âm
                        // thầm gán bác sĩ cho một giờ họ không trực.
                        const offDuty = s.doctorOnDuty === false;
                        return (
                          <Button
                            key={s.startTime}
                            type="button"
                            size="sm"
                            variant={selected ? "default" : "outline"}
                            disabled={!s.available && !offDuty}
                            className={offDuty ? "border-dashed text-muted-foreground" : undefined}
                            data-off-duty={offDuty || undefined}
                            onClick={() => {
                              if (offDuty) setDoctorId("");
                              setTime(label);
                            }}
                            title={
                              offDuty
                                ? "Bác sĩ không trực khung này — chọn sẽ bỏ bác sĩ, lễ tân chỉ định sau"
                                : (s.reason ?? "Trống")
                            }
                          >
                            {label}
                            {!s.available && s.reason ? ` · ${s.reason}` : ""}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              )
            )}
            {/* #19943 — chưa chọn bác sĩ, chế độ Theo giờ: lưới khung của ngày. */}
            {!hasLeoSlots && !doctorId && slotMinutes && slotChoices && (
              <div className="pt-1">
                {slotChoices.length === 0 ? (
                  !dayClosed && (
                    <p className="text-sm text-muted-foreground">
                      Ngày này không có khung giờ nào để đặt (chưa khai Buổi khám / ca trực, hoặc
                      đã qua giờ). Có thể nhập giờ thủ công nếu cần.
                    </p>
                  )
                ) : (
                  <div>
                    <p className="mb-1.5 text-sm text-muted-foreground">
                      Khung giờ trong ngày (mỗi khung {slotMinutes} phút) — bấm để chọn:
                    </p>
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Chọn khung giờ">
                      {slotChoices.map((c) => (
                        <Button
                          key={c.label}
                          type="button"
                          size="sm"
                          variant={c.label === time ? "default" : "outline"}
                          disabled={!c.available}
                          onClick={() => setTime(c.label)}
                          title={c.reason ?? "Trống"}
                        >
                          {c.label}
                          {c.reason ? ` · ${c.reason}` : ""}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* #18787 — a clash while the receptionist is still hunting for a free slot is
                INFORMATION, not a form error. It happened hundreds of times per session at one
                clinic, and a red alert that often stops meaning "something is wrong" and starts
                meaning "keep looking", which is the opposite of what a red alert is for. Amber +
                role="status" so a screen reader announces it politely rather than as an alert. */}
            {doctorId && conflicts.length > 0 && (
              <div
                className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                role="status"
              >
                <Info className="mt-0.5 size-4 shrink-0" />
                <div>
                  <strong>Khung giờ này đã có lịch.</strong> Bác sĩ đã có{" "}
                  <strong>{conflicts.length}</strong> lịch hẹn ở khung giờ này:
                  <ul className="mt-1.5 list-disc pl-4">
                    {conflicts.map((c) => (
                      <li key={c.id}>
                        <PatientLink id={c.patientId} name={c.patientName} /> ·{" "}
                        {c.serviceName ?? "—"} · {c.durationMinutes} phút
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {doctorId && slotMinutes && !conflictCheck.isLoading && conflicts.length === 0 && (
              <p className="flex items-center gap-1.5 text-sm text-emerald-600">
                <CheckCircle2 className="size-4 shrink-0" />
                Slot trống — không trùng lịch.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ghi chú lễ tân</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              className="flex w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="VD: Bệnh nhân muốn được nhắn Zalo trước 1 tiếng"
            />
          </CardContent>
        </Card>

        {errors._form && (
          <div
            className={
              isSlotConflict
                ? "rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                : "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            }
            role={isSlotConflict ? "status" : "alert"}
          >
            <div className="flex items-start gap-2">
              {isSlotConflict ? (
                <Info className="mt-0.5 size-4 shrink-0" />
              ) : (
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              )}
              <span>{errors._form}</span>
            </div>
            {/* A taken slot now offers the doctor's next free starts instead of leaving the
                receptionist to guess another time and press again (#18672). */}
            <SlotConflictHint
              error={create.error}
              takenStarts={takenStarts}
              onPick={(iso) => {
                const d = new Date(iso);
                if (Number.isNaN(d.getTime())) return;
                setDate(isoDate(d));
                setTime(
                  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
                );
                setErrors({});
              }}
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="outline">
            <Link to="/appointments">Hủy</Link>
          </Button>
          <Button type="submit" disabled={create.isPending || Boolean(dayClosed)}>
            {create.isPending ? "Đang đặt…" : "Đặt lịch"}
          </Button>
        </div>
      </form>
    </div>
  );
}
